import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { profileService } from '@/api';
import { useAuth } from '@/providers';
import { isAdminRole } from '@/types';

interface AdminRouteProps {
  children: React.ReactNode;
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F2F2]">
      <div className="text-center">
        <h1 className="text-6xl font-serif text-[#2E2A23] mb-4">404</h1>
        <p className="text-[#888185] mb-6">Página não encontrada</p>
        <a href="/" className="text-[#E94E1B] hover:underline">
          Voltar para o início
        </a>
      </div>
    </div>
  );
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileService.getProfile(),
    enabled: isAuthenticated,
  });

  if (isLoading || isLoadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F2F2]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#E94E1B] border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/feed" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if user has no profile
  if (!profile && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Check if user is admin - show 404 if not
  const profileData = profile as Record<string, unknown> | null;
  if (!isAdminRole(profileData?.role as string | undefined)) {
    return <NotFound />;
  }

  return <>{children}</>;
}
